/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Gantt, Willow } from '@svar-ui/react-gantt';
import type { IApi } from '@svar-ui/gantt-store';
import '@svar-ui/react-gantt/all.css';
import { Issue, IssueStatus, parseIssuesFromMarkdown } from '@shared/utils/issues';
import { Loader, Package, Sparkles, ZoomIn, ZoomOut } from 'lucide-react';
import { useApp } from '../../contexts/AppContext';
import { getRepoContext } from '../../utils/repoContext';
import { getIssuesMarkdown, IssueRepoContext } from '../../lib/api';
import { listAgentExecutions } from '../../lib/api/agent-executions';
import { generateVelocitySprintTasks } from '../../services/skillshop/velocity-sprint';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../components/ui/tooltip';

interface GanttTask {
  id: number;
  text: string;
  start: Date;
  end: Date;
  duration: number;
  progress: number;
  parent: number;
  type: string;
  lazy?: boolean;
  open?: boolean;
}

interface GanttLink {
  id: number;
  source: number;
  target: number;
  type: string;
}

const VELOCITY_SPRINT_PHASES = [
  {
    id: 'requirements',
    name: '1. Requirements',
    description: 'Product owners provide requirements (docs from Confluence, etc.)',
    durationDays: 1,
    color: '#8b5cf6',
  },
  {
    id: 'story-generation',
    name: '2. Story Drafts',
    description: 'Develop user story drafts using AI agents (Velocity)',
    durationDays: 1,
    color: '#3b82f6',
  },
  {
    id: 'story-curation',
    name: '3. Review & Wireframes',
    description: 'Review/curate User Stories, create wireframes with AI Agents',
    durationDays: 1,
    color: '#06b6d4',
  },
  {
    id: 'stakeholder-review',
    name: '4. Stakeholder Review',
    description: 'Supply user stories and wireframes to stakeholders for review',
    durationDays: 1,
    color: '#f59e0b',
  },
  {
    id: 'spec-finalization',
    name: '5. Spec Approval',
    description: 'After iterative review and approval → development-ready specs',
    durationDays: 1,
    color: '#10b981',
  },
  {
    id: 'code-generation',
    name: '6. Code Gen & Test',
    description: 'Generate code (using AI Agents), test new functionality (AI + Humans)',
    durationDays: 1,
    color: '#ef4444',
  },
  {
    id: 'release-feedback',
    name: '7. Release & Feedback',
    description: 'Release new app version weekly, supply to stakeholders for feedback',
    durationDays: 1,
    color: '#ec4899',
  },
];

const RELEASE_PHASE_COLORS: Record<string, string> = {
  'PROTOTYPE': '#8b5cf6',
  'MVP': '#3b82f6',
  'RC': '#f59e0b',
  'PRODUCTION': '#10b981',
};

const GANTT_TASK_TYPES = [
  { id: 'task', label: 'Task' },
  { id: 'summary', label: 'Summary' },
  { id: 'milestone', label: 'Milestone' },
  ...VELOCITY_SPRINT_PHASES.map(phase => ({
    id: phase.id,
    label: phase.name,
  })),
];

function getStatusProgress(status: IssueStatus): number {
  switch (status) {
    case 'Completed':
      return 100;
    case 'In Review':
      return 90;
    case 'Ready For Review':
      return 80;
    case 'In Progress':
      return 50;
    case 'Groomed':
      return 20;
    case 'Not Started':
    case 'Blocked':
    case 'On Hold':
    default:
      return 0;
  }
}

function getReleasePhase(title: string): string {
  const upperTitle = title.toUpperCase();
  if (upperTitle.includes('PROTOTYPE')) return 'PROTOTYPE';
  if (upperTitle.includes('MVP')) return 'MVP';
  if (upperTitle.includes('RC-') || upperTitle.includes('RELEASE CANDIDATE')) return 'RC';
  if (upperTitle.includes('PRODUCTION')) return 'PRODUCTION';
  return 'OTHER';
}

function getPhaseOrder(phase: string): number {
  const order: Record<string, number> = {
    'PROTOTYPE': 1,
    'MVP': 2,
    'RC': 3,
    'PRODUCTION': 4,
    'OTHER': 5,
  };
  return order[phase] || 5;
}

function addDays(date: Date, days: number): Date {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}

export function TimelineTab() {
  const { activeProduct, activeRepoUrl, isLoadingProduct } = useApp();
  const [releases, setReleases] = useState<Issue[]>([]);
  const [loadingReleases, setLoadingReleases] = useState(false);
  const [generatingSprintTasks, setGeneratingSprintTasks] = useState(false);
  const [activeSprintRunId, setActiveSprintRunId] = useState<string | null>(null);
  const [sprintError, setSprintError] = useState<string | null>(null);
  const [showSprintTasks, setShowSprintTasks] = useState(false);
  const [sprintTasks, setSprintTasks] = useState<Issue[]>([]);
  
  const ganttApiRef = useRef<IApi | null>(null);

  const handleZoomIn = useCallback(() => {
    if (ganttApiRef.current) {
      ganttApiRef.current.exec('zoom-scale', { dir: 1, date: new Date() });
    }
  }, []);

  const handleZoomOut = useCallback(() => {
    if (ganttApiRef.current) {
      ganttApiRef.current.exec('zoom-scale', { dir: -1, date: new Date() });
    }
  }, []);

  const repoContext = useMemo(
    () => getRepoContext(activeProduct ?? null, activeRepoUrl),
    [activeProduct, activeRepoUrl],
  );

  const releaseRepoContext = useMemo((): IssueRepoContext => ({
    repoUrl: repoContext.repoUrl,
    repoPath: repoContext.repoPath,
  }), [repoContext]);

  const loadReleases = useCallback(async () => {
    setLoadingReleases(true);
    try {
      const result = await getIssuesMarkdown(releaseRepoContext);
      const parsedIssues = parseIssuesFromMarkdown(result.content || '');
      
      const releaseIssues = parsedIssues.filter(issue => issue.type === 'Release');
      
      releaseIssues.sort((a, b) => {
        const dateA = new Date(a.createdAt || 0);
        const dateB = new Date(b.createdAt || 0);
        return dateA.getTime() - dateB.getTime();
      });
      
      setReleases(releaseIssues);
    } catch (error) {
      console.error('[TimelineTab] Error loading releases:', error);
    } finally {
      setLoadingReleases(false);
    }
  }, [releaseRepoContext]);

  const loadSprintTasks = useCallback(async () => {
    try {
      const result = await getIssuesMarkdown(releaseRepoContext);
      const parsedIssues = parseIssuesFromMarkdown(result.content || '');
      
      const velocitySprintTasks = parsedIssues.filter(issue => 
        issue.type === 'Task' && 
        issue.labels?.includes('velocity-sprint')
      );
      
      setSprintTasks(velocitySprintTasks);
    } catch (error) {
      console.error('[TimelineTab] Error loading sprint tasks:', error);
    }
  }, [releaseRepoContext]);

  const handleGenerateSprintTasks = useCallback(async () => {
    if (!activeProduct) {
      setSprintError('No active product selected');
      return;
    }

    setGeneratingSprintTasks(true);
    setSprintError(null);

    try {
      const result = await generateVelocitySprintTasks(
        releases,
        activeProduct,
        VELOCITY_SPRINT_PHASES
      );

      if (result.success) {
        console.log('[TimelineTab] Velocity Sprint tasks generated successfully');
        await loadReleases();
        await loadSprintTasks();
        setShowSprintTasks(true);
      } else {
        setSprintError(result.error || 'Failed to generate Velocity Sprint tasks');
      }
    } catch (error: any) {
      console.error('[TimelineTab] Error generating Velocity Sprint tasks:', error);
      setSprintError(error.message || 'Failed to generate Velocity Sprint tasks');
    } finally {
      setGeneratingSprintTasks(false);
      setActiveSprintRunId(null);
    }
  }, [activeProduct, releases, loadReleases, loadSprintTasks]);

  useEffect(() => {
    if (isLoadingProduct) {
      return;
    }
    
    if (!activeProduct) {
      setReleases([]);
      setSprintTasks([]);
      return;
    }
    
    void loadReleases();
    void loadSprintTasks();
  }, [loadReleases, loadSprintTasks, isLoadingProduct, activeProduct]);

  const syncActiveSprintExecution = useCallback(async () => {
    if (!activeProduct) return;
    
    const res = await listAgentExecutions({ status: 'pending,running', limit: 100 });
    if (!res.success || !res.executions) return;
    
    let foundActiveRun = false;
    for (const exec of res.executions) {
      const meta = exec.metadata && typeof exec.metadata === 'object' 
        ? exec.metadata as Record<string, unknown> 
        : null;
      
      if (meta?.requestType === 'velocity_sprint_generation' && 
          meta?.productId === activeProduct.id) {
        setGeneratingSprintTasks(true);
        setActiveSprintRunId(exec.id);
        foundActiveRun = true;
        break;
      }
    }
    
    if (!foundActiveRun) {
      setActiveSprintRunId(prevId => {
        if (prevId) {
          setGeneratingSprintTasks(false);
          loadReleases();
          loadSprintTasks();
        }
        return null;
      });
    }
  }, [activeProduct, loadReleases, loadSprintTasks]);

  useEffect(() => {
    if (!activeProduct) return;
    syncActiveSprintExecution();
  }, [activeProduct, syncActiveSprintExecution]);

  useEffect(() => {
    if (!generatingSprintTasks) return;
    const interval = setInterval(syncActiveSprintExecution, 4000);
    return () => clearInterval(interval);
  }, [generatingSprintTasks, syncActiveSprintExecution]);

  const { tasks, links } = useMemo(() => {
    const ganttTasks: GanttTask[] = [];
    const ganttLinks: GanttLink[] = [];
    let taskId = 1;
    let linkId = 1;

    const now = new Date();
    const RELEASE_DURATION_DAYS = 7;

    const sortedReleases = [...releases].sort((a, b) => {
      const phaseA = getPhaseOrder(getReleasePhase(a.title));
      const phaseB = getPhaseOrder(getReleasePhase(b.title));
      if (phaseA !== phaseB) return phaseA - phaseB;
      
      const versionA = a.version || '0.0.0';
      const versionB = b.version || '0.0.0';
      return versionA.localeCompare(versionB, undefined, { numeric: true });
    });

    const productSummaryId = taskId++;
    
    let projectStart = sortedReleases.length > 0 && sortedReleases[0].createdAt
      ? new Date(sortedReleases[0].createdAt)
      : now;
    
    const projectEnd = addDays(projectStart, sortedReleases.length * RELEASE_DURATION_DAYS + 14);

    ganttTasks.push({
      id: productSummaryId,
      text: activeProduct?.title || 'Product Development',
      start: projectStart,
      end: projectEnd,
      duration: Math.ceil((projectEnd.getTime() - projectStart.getTime()) / (1000 * 60 * 60 * 24)),
      progress: sortedReleases.length > 0 
        ? Math.round(sortedReleases.reduce((sum, r) => sum + getStatusProgress(r.status), 0) / sortedReleases.length)
        : 0,
      parent: 0,
      type: 'summary',
      open: true,
    });

    const phaseGroups: Record<string, { id: number; start: Date; releases: Issue[] }> = {};
    let currentWeekStart = new Date(projectStart);

    sortedReleases.forEach((release) => {
      const phase = getReleasePhase(release.title);
      if (!phaseGroups[phase]) {
        const phaseId = taskId++;
        phaseGroups[phase] = {
          id: phaseId,
          start: new Date(currentWeekStart),
          releases: [],
        };
      }
      phaseGroups[phase].releases.push(release);
    });

    const phaseOrder = ['PROTOTYPE', 'MVP', 'RC', 'PRODUCTION', 'OTHER'];
    let previousPhaseId: number | null = null;
    let weekOffset = 0;

    phaseOrder.forEach((phaseName) => {
      const phaseGroup = phaseGroups[phaseName];
      if (!phaseGroup || phaseGroup.releases.length === 0) return;

      const phaseStartDate = addDays(projectStart, weekOffset * RELEASE_DURATION_DAYS);
      const phaseEndDate = addDays(phaseStartDate, phaseGroup.releases.length * RELEASE_DURATION_DAYS);
      
      ganttTasks.push({
        id: phaseGroup.id,
        text: `${phaseName} Phase (${phaseGroup.releases.length} releases)`,
        start: phaseStartDate,
        end: phaseEndDate,
        duration: phaseGroup.releases.length * RELEASE_DURATION_DAYS,
        progress: Math.round(
          phaseGroup.releases.reduce((sum, r) => sum + getStatusProgress(r.status), 0) / phaseGroup.releases.length
        ),
        parent: productSummaryId,
        type: 'summary',
        open: true,
      });

      if (previousPhaseId !== null) {
        ganttLinks.push({
          id: linkId++,
          source: previousPhaseId,
          target: phaseGroup.id,
          type: 'e2s',
        });
      }
      previousPhaseId = phaseGroup.id;

      let previousReleaseId: number | null = null;

      phaseGroup.releases.forEach((release, index) => {
        const releaseId = taskId++;
        const releaseStart = addDays(phaseStartDate, index * RELEASE_DURATION_DAYS);
        const releaseEnd = addDays(releaseStart, RELEASE_DURATION_DAYS);

        const releaseTitle = release.title.replace(/^(PROTOTYPE|MVP|RC|PRODUCTION)-\d+:\s*/i, '');
        
        ganttTasks.push({
          id: releaseId,
          text: `${release.version || 'TBD'}: ${releaseTitle}`,
          start: releaseStart,
          end: releaseEnd,
          duration: RELEASE_DURATION_DAYS,
          progress: getStatusProgress(release.status),
          parent: phaseGroup.id,
          type: release.status === 'Completed' ? 'milestone' : 'task',
          open: showSprintTasks,
        });

        if (showSprintTasks) {
          const releaseSprintTasks = sprintTasks.filter(task => 
            task.links?.some(link => link.includes(release.id))
          );

          let phaseStart = new Date(releaseStart);
          let previousSprintTaskId: number | null = null;

          VELOCITY_SPRINT_PHASES.forEach((phase) => {
            const matchingTask = releaseSprintTasks.find(task => 
              task.labels?.includes(phase.id)
            );

            const sprintTaskId = taskId++;
            const phaseEnd = addDays(phaseStart, phase.durationDays);

            ganttTasks.push({
              id: sprintTaskId,
              text: matchingTask ? `${phase.name} - ${matchingTask.status}` : phase.name,
              start: phaseStart,
              end: phaseEnd,
              duration: phase.durationDays,
              progress: matchingTask ? getStatusProgress(matchingTask.status) : 0,
              parent: releaseId,
              type: phase.id,
            });

            if (previousSprintTaskId !== null) {
              ganttLinks.push({
                id: linkId++,
                source: previousSprintTaskId,
                target: sprintTaskId,
                type: 'e2s',
              });
            }

            previousSprintTaskId = sprintTaskId;
            phaseStart = phaseEnd;
          });
        }

        if (previousReleaseId !== null) {
          ganttLinks.push({
            id: linkId++,
            source: previousReleaseId,
            target: releaseId,
            type: 'e2s',
          });
        }
        previousReleaseId = releaseId;

        weekOffset++;
      });
    });

    return { tasks: ganttTasks, links: ganttLinks };
  }, [releases, activeProduct, showSprintTasks, sprintTasks]);

  return (
    <>
      {/* Velocity Sprint Info Banner */}
      <div 
        className="mb-4 p-4 rounded-lg border"
        style={{ 
          backgroundColor: '#f0f9ff',
          borderColor: '#bae6fd',
        }}
      >
        <div className="flex items-start justify-between">
          <div>
            <h4 className="text-sm font-semibold mb-1 text-blue-800">Velocity Sprint - Weekly Development Cycle</h4>
            <p className="text-xs text-blue-600 mb-3">
              Spec-Driven Development with AI Agents: Each release follows this 7-step workflow
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="text-xs text-blue-500 bg-blue-50 px-2 py-1 rounded">
              ~1 week per release
            </div>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    className="btn-compact"
                    onClick={handleGenerateSprintTasks}
                    disabled={generatingSprintTasks || releases.length === 0 || !activeProduct}
                    style={{ backgroundColor: '#6366f1' }}
                  >
                    {generatingSprintTasks ? (
                      <>
                        <Loader size={14} className="animate-spin" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <Sparkles size={14} />
                        Generate Sprint Tasks
                      </>
                    )}
                  </button>
                </TooltipTrigger>
                <TooltipContent side="left" className="max-w-xs">
                  <p className="text-xs">
                    Create Task issues for each Velocity Sprint workflow phase for your releases.
                    Idempotent: won't create duplicates if tasks already exist.
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </div>
        {sprintError && (
          <div className="mt-2 text-xs text-red-600 bg-red-50 px-2 py-1 rounded">
            {sprintError}
          </div>
        )}
        <div className="flex flex-wrap gap-1">
          {VELOCITY_SPRINT_PHASES.map((phase, index) => (
            <TooltipProvider key={phase.id}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex items-center">
                    <div 
                      className="px-2 py-1 rounded-l text-xs text-white cursor-help"
                      style={{ backgroundColor: phase.color }}
                    >
                      {phase.name}
                    </div>
                    {index < VELOCITY_SPRINT_PHASES.length - 1 && (
                      <div className="text-gray-400 px-1">→</div>
                    )}
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <p className="text-xs font-medium">{phase.description}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ))}
        </div>
      </div>

      {/* Sprint Tasks Filter Toggle and Zoom Controls */}
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex items-center gap-2">
                  <Switch
                    id="sprint-tasks-filter"
                    checked={showSprintTasks}
                    onCheckedChange={setShowSprintTasks}
                    disabled={sprintTasks.length === 0}
                  />
                  <Label 
                    htmlFor="sprint-tasks-filter" 
                    className={`flex items-center gap-1 cursor-pointer ${sprintTasks.length === 0 ? 'text-muted-foreground' : ''}`}
                  >
                    Show Sprint Tasks
                    {sprintTasks.length > 0 && (
                      <span className="text-xs text-muted-foreground">({sprintTasks.length})</span>
                    )}
                  </Label>
                </div>
              </TooltipTrigger>
              <TooltipContent side="right" className="max-w-xs">
                <p className="text-xs">
                  {sprintTasks.length === 0 
                    ? 'No Sprint tasks found. Use "Generate Sprint Tasks" to create them.'
                    : 'Show Velocity Sprint workflow tasks as sub-items under each release on the Gantt chart.'}
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        
        {/* Zoom Controls */}
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground mr-1">Zoom:</span>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={handleZoomOut}
                  className="p-1.5 rounded hover:bg-gray-100 text-gray-600 hover:text-gray-800 transition-colors"
                  disabled={releases.length === 0}
                >
                  <ZoomOut size={18} />
                </button>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                <p className="text-xs">Zoom out (show more time)</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={handleZoomIn}
                  className="p-1.5 rounded hover:bg-gray-100 text-gray-600 hover:text-gray-800 transition-colors"
                  disabled={releases.length === 0}
                >
                  <ZoomIn size={18} />
                </button>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                <p className="text-xs">Zoom in (show less time)</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>

      {/* Gantt Chart */}
      {loadingReleases || isLoadingProduct ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
          <Loader size={48} style={{ marginBottom: '16px', opacity: 0.5, animation: 'spin 1s linear infinite' }} />
          <p>Loading project timeline...</p>
        </div>
      ) : releases.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
          <Package size={48} style={{ marginBottom: '16px', opacity: 0.5 }} />
          <p>No releases found for this product.</p>
          <p style={{ fontSize: '14px', marginTop: '8px' }}>
            Create releases in the Releases page to see them on the project timeline.
          </p>
        </div>
      ) : (
        <div 
          style={{ 
            height: 'calc(100vh - 400px)',
            minHeight: '400px',
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            overflow: 'auto',
          }}
        >
          <Willow>
            <Gantt
              tasks={tasks}
              links={links}
              scales={[
                { unit: 'month', step: 1, format: (date: Date) => date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) },
                { unit: 'week', step: 1, format: (date: Date) => `W${Math.ceil(date.getDate() / 7)}` },
              ]}
              taskTypes={GANTT_TASK_TYPES}
              zoom={true}
              cellWidth={60}
              cellHeight={38}
              scaleHeight={60}
              init={(api) => { ganttApiRef.current = api; }}
            />
          </Willow>
        </div>
      )}

      {/* Summary Stats */}
      {!loadingReleases && releases.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-4 text-sm text-muted-foreground">
          <div>
            <span className="font-medium">{releases.length}</span> releases total
          </div>
          {['PROTOTYPE', 'MVP', 'RC', 'PRODUCTION'].map(phase => {
            const count = releases.filter(r => getReleasePhase(r.title) === phase).length;
            if (count === 0) return null;
            return (
              <div key={phase} className="flex items-center gap-1">
                <span 
                  className="w-2 h-2 rounded-full" 
                  style={{ backgroundColor: RELEASE_PHASE_COLORS[phase] }}
                />
                <span className="font-medium">{count}</span> {phase.toLowerCase()}
              </div>
            );
          })}
          <div className="border-l pl-4 ml-2">
            <span className="font-medium">
              {releases.filter(r => r.status === 'Completed').length}
            </span> completed
          </div>
          <div className="ml-auto">
            <span className="font-medium">{VELOCITY_SPRINT_PHASES.length}</span> sprint phases per release
            {showSprintTasks && sprintTasks.length > 0 && (
              <span className="ml-2 text-blue-600">
                ({sprintTasks.length} sprint tasks shown)
              </span>
            )}
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        
        .wx-gantt .wx-bar.wx-task.requirements {
          background-color: #8b5cf6;
          border-color: #7c3aed;
        }
        .wx-gantt .wx-bar.wx-task.requirements .wx-progress-percent {
          background-color: #7c3aed;
        }
        .wx-gantt .wx-bar.wx-task.story-generation {
          background-color: #3b82f6;
          border-color: #2563eb;
        }
        .wx-gantt .wx-bar.wx-task.story-generation .wx-progress-percent {
          background-color: #2563eb;
        }
        .wx-gantt .wx-bar.wx-task.story-curation {
          background-color: #06b6d4;
          border-color: #0891b2;
        }
        .wx-gantt .wx-bar.wx-task.story-curation .wx-progress-percent {
          background-color: #0891b2;
        }
        .wx-gantt .wx-bar.wx-task.stakeholder-review {
          background-color: #f59e0b;
          border-color: #d97706;
        }
        .wx-gantt .wx-bar.wx-task.stakeholder-review .wx-progress-percent {
          background-color: #d97706;
        }
        .wx-gantt .wx-bar.wx-task.spec-finalization {
          background-color: #10b981;
          border-color: #059669;
        }
        .wx-gantt .wx-bar.wx-task.spec-finalization .wx-progress-percent {
          background-color: #059669;
        }
        .wx-gantt .wx-bar.wx-task.code-generation {
          background-color: #ef4444;
          border-color: #dc2626;
        }
        .wx-gantt .wx-bar.wx-task.code-generation .wx-progress-percent {
          background-color: #dc2626;
        }
        .wx-gantt .wx-bar.wx-task.release-feedback {
          background-color: #ec4899;
          border-color: #db2777;
        }
        .wx-gantt .wx-bar.wx-task.release-feedback .wx-progress-percent {
          background-color: #db2777;
        }
      `}</style>
    </>
  );
}
